import { useMemo } from 'react';
import {
  EntityId,
  JobPosition,
  JobPositionAssessmentQuestion,
  JobPositionTask,
  JPClosingMotivation,
  JobPositionStats,
} from '@restworld/utility-types';

import { ENDPOINTS } from '../../endpoints';
import { Service } from '../base';
import dynamicAxios from '../../utils/axios';
import {
  ActiveJobPosition,
  IndexActiveJobPositions,
  IndexJobPositions,
  IndexReferralJobPosition,
  JobApplicationPerformance,
  JobPositionFilterType,
  SimilarPositionType,
  ApplicationPerformanceWorkerMetricsType,
  ChatCompletionType,
  PromptItem,
  GenerateAdContentResponse,
  UTMSourceType,
  JobLocationCoordinates,
  JobPositionListParamsType,
  ExploreJobPositionsGraphql,
  JobCoordinateParamsType,
  ListJobPositions,
  RequestToPublishResponse,
  LatestValidationResponse,
  ValidateAdResponse,
  PaymentReadinessResponse,
  JobDoctorAnalysisResponse,
  JobDoctorPendingResponse,
} from './types';
import { Id } from '../../utils/common_types';
import useServices from '../../hooks/useServices';
import { exploreJobPositionsQuery, exploreStaticJobPositionsQuery, indexJobPositionsQuery } from './graphqlQueries';
import { AxiosRequestConfig } from 'axios';

class ServiceJobPositions extends Service {
  // deprecate after merging the current implementation with the new grapqhl query
  fetchJobPositions({
    restaurantId,
    limit,
    offset,
    order_by,
    filters,
  }: {
    restaurantId?: string;
    limit?: number;
    offset?: number;
    order_by?: Array<string>;
    filters?: JobPositionFilterType;
  }) {
    const appliedFilters = filters || {};

    return this.axiosInstance.get<IndexJobPositions>(ENDPOINTS.jobPositions.root, {
      params: { limit, offset, restaurant_id: restaurantId, ...appliedFilters, order_by },
    });
  }

  listJobPositions({
    limit,
    offset,
    order_by,
    employer_id,
    search_vector,
    organization_id,
    restaurant_name,
    status,
    locality,
    occupation_id,
    show_performance_metrics,
  }: {
    limit: number;
    offset: number;
    order_by?: Array<string>;
    employer_id?: string;
    search_vector?: string;
    organization_id?: string;
    restaurant_name?: Array<string>;
    status?: string;
    locality?: Array<string>;
    occupation_id?: string;
    show_performance_metrics?: boolean;
  }) {
    return this.axiosInstance.post<ListJobPositions>(ENDPOINTS.employers.graphql, {
      query: indexJobPositionsQuery,
      variables: {
        limit,
        offset,
        order_by,
        employer_id,
        search_vector,
        organization_id,
        restaurant_name,
        status,
        locality,
        occupation_id,
        show_performance_metrics,
      },
    });
  }

  duplicate({
    jobPositionId,
    restaurantId,
    params,
  }: {
    jobPositionId: EntityId;
    restaurantId?: string;
    params?: Partial<JobPosition>;
  }) {
    return this.axiosInstance.post<{ id: string }>(ENDPOINTS.jobPositions.duplicate, {
      restaurant_id: restaurantId,
      job_position_id: jobPositionId,
      ...(params || {}),
    });
  }

  showJobPosition(jobPositionId: EntityId) {
    return this.axiosInstance.get<JobPosition>(`${ENDPOINTS.jobPositions.root}/${jobPositionId}`);
  }

  exportApplicationsCsv(jobPositionId: EntityId) {
    return this.axiosInstance.get<Blob>(`${ENDPOINTS.jobPositions.root}/${jobPositionId}/applications/export.csv`, {
      responseType: 'blob',
    });
  }

  showJobPositionStats(jobPositionId: EntityId) {
    return this.axiosInstance.get<JobPositionStats>(`${ENDPOINTS.jobPositions.stats}`, {
      params: {
        job_position_id: jobPositionId,
      },
    });
  }

  // To check if the JP is active or not
  showJobPositionStatus(jobPositionId: EntityId, check_active_job_applications?: boolean) {
    return this.axiosInstance.get<boolean>(`${ENDPOINTS.jobPositions.active_status}/${jobPositionId}`, {
      params: { check_active_job_applications },
    });
  }

  updateJobPosition({ jobPositionId, params }: { jobPositionId: EntityId; params: Partial<JobPosition> }) {
    return this.axiosInstance.put<Id>(`${ENDPOINTS.jobPositions.root}/${jobPositionId}`, params);
  }

  updateJobPositionStatus({
    jobPositionId,
    newStatusKey,
    params,
  }: {
    jobPositionId: EntityId;
    newStatusKey: string;
    params?: Partial<JPClosingMotivation>;
  }) {
    return this.axiosInstance.patch<string>(`${ENDPOINTS.jobPositions.status}/${jobPositionId}`, {
      status_key: newStatusKey,
      ...params,
    });
  }

  deleteJobPosition(jobPositionId?: string) {
    return this.axiosInstance.delete<string>(`${ENDPOINTS.jobPositions.root}/${jobPositionId}`);
  }

  putOfflineJobPosition(jobPositionId: string, delaySeconds?: number) {
    return this.axiosInstance.patch<string>(`${ENDPOINTS.jobPositions.root}/offline`, {
      id: jobPositionId,
      ...(delaySeconds !== undefined && { delay_seconds: delaySeconds }),
    });
  }

  assignCsm({ userId, jobPositionId }: { userId: string; jobPositionId: EntityId }) {
    return this.axiosInstance.post<string>(ENDPOINTS.jobPositions.assignCsm, {
      customer_success_manager_id: userId,
      job_position_id: jobPositionId,
    });
  }

  makeCsmMain({ userId, jobPositionId }: { userId: string; jobPositionId: EntityId }) {
    return this.axiosInstance.post<string>(ENDPOINTS.jobPositions.makeCsmMain, {
      customer_success_manager_id: userId,
      job_position_id: jobPositionId,
    });
  }

  removeCsm({ userId, jobPositionId }: { userId: string; jobPositionId: EntityId }) {
    return this.axiosInstance.delete<string>(ENDPOINTS.jobPositions.removeCsm, {
      data: {
        customer_success_manager_id: userId,
        job_position_id: jobPositionId,
      },
    });
  }

  addTask({ jobPositionId, task }: { jobPositionId: EntityId; task: Partial<JobPositionTask> }) {
    return this.axiosInstance.post<JobPositionTask>(ENDPOINTS.jobPositions.task, {
      ...task,
      job_position_id: jobPositionId,
    });
  }

  updateTask({ taskId, params }: { taskId: number; params: Partial<JobPositionTask> }) {
    return (
      this,
      this.axiosInstance.patch<JobPositionTask>(ENDPOINTS.jobPositions.task, {
        id: taskId,
        ...params,
        category: params.category_var?.id || params.category,
        sub_category: params.sub_category_var?.id || params.sub_category,
      })
    );
  }

  deleteTask(taskId: number) {
    return this.axiosInstance.delete<string>(ENDPOINTS.jobPositions.task, { data: { id: taskId } });
  }

  getTask(taskId: number) {
    return this.axiosInstance.get<JobPositionTask>(ENDPOINTS.jobPositions.task, { params: { id: taskId } });
  }

  getJobPositionApplicationPerformanceMetrics(jobPositionId: EntityId) {
    return this.axiosInstance.get<JobApplicationPerformance>(ENDPOINTS.jobPositions.applicationPerformanceMetrics, {
      params: { job_position_id: jobPositionId },
    });
  }

  indexPublicJobPositions() {
    return this.axiosInstance.get<IndexActiveJobPositions>(`${ENDPOINTS.jobPositions.root}/active`);
  }

  fetchActiveJobPosition(id: string) {
    return this.axiosInstance.get<ActiveJobPosition>(`${ENDPOINTS.jobPositions.root}/active/${id}`);
  }

  indexReferralJobPositions() {
    return this.axiosInstance.get<IndexReferralJobPosition>(ENDPOINTS.jobPositions.referralJobPositions);
  }

  createJobPositionViewLog({
    jobPositionId,
    ambassadorCode,
    utmSource,
    isViola,
    ipAddress,
  }: {
    jobPositionId: EntityId;
    ambassadorCode?: string;
    utmSource?: UTMSourceType;
    isViola?: boolean;
    ipAddress?: string;
  }) {
    return this.axiosInstance.post<string>(ENDPOINTS.jobPositions.jobPositionViewLog, {
      job_position_id: jobPositionId,
      ambassador_code: ambassadorCode,
      utm_source: utmSource,
      is_viola: isViola,
      id_address: ipAddress,
    });
  }

  createJobPosition({ params }: { params: Partial<JobPosition> }) {
    return this.axiosInstance.post<Id>(ENDPOINTS.jobPositions.root, {
      ...params,
    });
  }

  similarJobPositions(jobPositionId: EntityId, opts?: { limit?: number; offset?: number }) {
    return this.axiosInstance.get<SimilarPositionType[]>(`${ENDPOINTS.jobPositions.similarJobs}/${jobPositionId}`, {
      params: {
        ...(opts?.limit != null ? { limit: opts.limit } : {}),
        ...(opts?.offset != null ? { offset: opts.offset } : {}),
      },
    });
  }

  createFavoriteJobPosition({ jobPositionId, email }: { jobPositionId: EntityId; email: string }) {
    return this.axiosInstance.post<{
      id: number;
      email?: string;
    }>(`${ENDPOINTS.jobPositions.favoriteJob}`, {
      job_position_id: jobPositionId,
      email,
    });
  }

  isJobPositionFavorite(jobPositionId: EntityId) {
    return this.axiosInstance.get(`${ENDPOINTS.jobPositions.isJobFavorite}/${jobPositionId}`);
  }

  deleteFavoriteJobPosition(jobPositionId: EntityId) {
    return this.axiosInstance.delete(`${ENDPOINTS.jobPositions.favoriteJob}/${jobPositionId}`);
  }

  getJobPositionApplicationPerformanceWorkerMetrics(jobPositionId: EntityId) {
    return this.axiosInstance.get<ApplicationPerformanceWorkerMetricsType>(
      ENDPOINTS.jobPositions.applicationPerformanceMetrics,
      {
        params: { job_position_id: jobPositionId },
      }
    );
  }
  showGenerationPrompt(jobPositionId: EntityId, category: string) {
    return this.axiosInstance.get<Array<PromptItem>>(ENDPOINTS.jobPositions.showGenerationPrompt, {
      params: { job_position_id: jobPositionId, category },
    });
  }

  chatCompletion(jobPositionId: EntityId, category: string, inputPrompt: Array<PromptItem>) {
    return this.axiosInstance.get<ChatCompletionType>(ENDPOINTS.jobPositions.chatCompletion, {
      params: {
        job_position_id: jobPositionId,
        category,
        input_prompt: inputPrompt,
      },
    });
  }

  /**
   * Generates job title, description, and contract description in a single call.
   * The content is automatically saved to the database.
   * @param jobPositionId - The ID of the job position
   * @returns Object with job_title, job_description, and job_contract_description
   */
  generateAdContent(jobPositionId: EntityId) {
    return this.axiosInstance.post<GenerateAdContentResponse>(ENDPOINTS.jobPositions.generateAdContent, null, {
      params: {
        job_position_id: jobPositionId,
      },
    });
  }

  /**
   * Generates ad content from raw JP data (for org onboarding when JP is not yet in DB).
   * Sends the JP form data directly to the backend for AI generation.
   * @param jpData - The job position form data
   * @returns Object with job_title, job_description, and job_contract_description
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  generateAdContentFromData(jpData: Record<string, any>) {
    return this.axiosInstance.post<GenerateAdContentResponse>(ENDPOINTS.jobPositions.generateAdContent, {
      job_position_data: jpData,
    });
  }

  /**
   * @deprecated Use generateAdContent() instead. This endpoint will be removed in a future version.
   */
  createJobTitle(jobPositionId: EntityId) {
    return this.axiosInstance.get<ChatCompletionType>(ENDPOINTS.jobPositions.createJobTitle, {
      params: {
        job_position_id: jobPositionId,
      },
    });
  }

  /**
   * @deprecated Use generateAdContent() instead. This endpoint will be removed in a future version.
   */
  createJobDescription(jobPositionId: EntityId) {
    return this.axiosInstance.get<ChatCompletionType>(ENDPOINTS.jobPositions.createJobDescription, {
      params: {
        job_position_id: jobPositionId,
      },
    });
  }

  /**
   * @deprecated Use generateAdContent() instead. This endpoint will be removed in a future version.
   */
  createJobContractDescription(jobPositionId: EntityId) {
    return this.axiosInstance.get<ChatCompletionType>(ENDPOINTS.jobPositions.createJobContractDescription, {
      params: {
        job_position_id: jobPositionId,
      },
    });
  }

  indexRegions() {
    return this.axiosInstance.get<string[]>(ENDPOINTS.jobPositions.regions);
  }

  getJobLocationCoordinates(params: JobCoordinateParamsType, config?: AxiosRequestConfig) {
    return this.axiosInstance.get<JobLocationCoordinates[]>(ENDPOINTS.jobPositions.JobLocationCoordinates, {
      params,
      ...config,
    });
  }

  async getJobLocationPositionList(params: JobPositionListParamsType, config?: AxiosRequestConfig) {
    // Route the occupation filter to the correctly-typed GraphQL variable by value shape:
    // an array of ids must go on `occupation_ids: [String]`; a single id stays on `occupation_id: Int`.
    // Saved-research email "Vedi tutte" links carry occupation_id as an array, which the scalar
    // `$occupation_id: Int` rejects ("Argument occupation_id has invalid value"). Normalizing here
    // fixes already-delivered emails and any future/other caller without a backend schema change.
    const { occupation_id, ...rest } = params as JobPositionListParamsType & {
      occupation_id?: string | number | string[];
    };
    const variables: Record<string, unknown> = { ...rest };
    if (Array.isArray(occupation_id)) {
      variables.occupation_ids = variables.occupation_ids ?? occupation_id.map(String);
    } else if (occupation_id !== undefined && occupation_id !== null) {
      variables.occupation_id = Number(occupation_id);
    }

    const r = await this.axiosInstance.post<ExploreJobPositionsGraphql>(
      ENDPOINTS.employers.graphql,
      {
        query: exploreJobPositionsQuery,
        variables,
      },
      {
        ...config,
      }
    );
    return r;
  }

  fetchJobPositionsGroupedCount() {
    return this.axiosInstance.get(ENDPOINTS.jobPositions.groupedCount, {
      params: { count: 'domain_interest' },
    });
  }

  exploreStaticJobPositions({
    occupationId,
    restaurantId,
    employerId,
  }: {
    occupationId?: number;
    restaurantId?: string;
    employerId?: string;
  }) {
    return this.axiosInstance.post(ENDPOINTS.employers.graphql, {
      query: exploreStaticJobPositionsQuery,
      variables: { occupation_id: occupationId, employer_id: employerId, restaurant_id: restaurantId },
    });
  }

  assignJuniorRecruiter({ jobPositionId, juniorRecruiterId }: { jobPositionId?: string; juniorRecruiterId?: string }) {
    return this.axiosInstance.post<string>(ENDPOINTS.jobPositions.assignJR, {
      job_position_id: jobPositionId,
      junior_recruiter_id: juniorRecruiterId,
    });
  }

  requestJuniorRecruiter({ jobPositionId, requestedJr }: { jobPositionId?: string; requestedJr?: boolean }) {
    return this.axiosInstance.post<string>(`${ENDPOINTS.jobPositions.root}/request_assistance`, {
      job_position_id: jobPositionId,
      requested_jr: requestedJr,
    });
  }

  setSeoData({
    job_position_id,
    meta_background_image,
    meta_image,
    meta_image_text_tag_1,
    meta_image_text_tag_2,
    meta_image_text_tag_3,
    meta_description,
    meta_title,
  }: {
    job_position_id: string;
    meta_background_image?: File;
    meta_image?: File;
    meta_image_text_tag_1?: string;
    meta_image_text_tag_2?: string;
    meta_image_text_tag_3?: string;
    meta_description?: string;
    meta_title?: string;
  }) {
    const formData = new FormData();
    formData.append('id', job_position_id);
    if (meta_background_image) formData.append('meta_background_image', meta_background_image);
    if (meta_image) formData.append('meta_image', meta_image);
    if (meta_image_text_tag_1) formData.append('meta_image_text_tag_1', meta_image_text_tag_1);
    if (meta_image_text_tag_2) formData.append('meta_image_text_tag_2', meta_image_text_tag_2);
    if (meta_image_text_tag_3) formData.append('meta_image_text_tag_3', meta_image_text_tag_3);
    if (meta_description) formData.append('meta_description', meta_description);
    if (meta_title) formData.append('meta_title', meta_title);

    return this.axiosInstance.post<JobPosition>(`${ENDPOINTS.jobPositions.root}/seo_metadata`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
  }

  isJobPositionActive({
    jobPositionId,
    check_active_job_applications,
  }: {
    jobPositionId?: string;
    check_active_job_applications?: boolean;
  }) {
    return this.axiosInstance.get<boolean>(`${ENDPOINTS.jobPositions.active_status}/${jobPositionId}`, {
      params: {
        check_active_job_applications,
      },
    });
  }

  requestToPublishJobPosition({ jobPositionId }: { jobPositionId?: string }) {
    return this.axiosInstance.post<RequestToPublishResponse>(
      `${ENDPOINTS.jobPositions.root}/${jobPositionId}/request_to_publish`
    );
  }

  getLatestValidation({ jobPositionId }: { jobPositionId: string }) {
    return this.axiosInstance.get<LatestValidationResponse>(
      `${ENDPOINTS.jobPositions.root}/${jobPositionId}/latest_validation`
    );
  }

  // Job Doctor (Dr. Job): the most recent analysis for a JP. Org-scoped + rollout-gated
  // on the backend; returns 404 when the JP is out of the rollout or has no analysis.
  getLatestJobDoctorAnalysis(jobPositionId: EntityId) {
    return this.axiosInstance.get<JobDoctorAnalysisResponse>(
      `${ENDPOINTS.jobPositions.root}/${jobPositionId}/analysis`
    );
  }

  // Delay system: queue an accepted lever for debounced application. Nothing is applied
  // immediately — a background job applies the batch a few minutes after the last accept, and
  // the analysis refreshes on the daily run. Returns the updated pending set.
  scheduleJobDoctorLever(analysisId: EntityId, lever: string) {
    return this.axiosInstance.post<JobDoctorPendingResponse>(
      `${ENDPOINTS.jobPositions.root}/analyses/${analysisId}/pending_levers`,
      { lever }
    );
  }

  // Delay system: undo a still-pending lever (mis-click safety net). No-op once the debounce
  // has fired. The lever key can contain '+', so it goes in the body, not the path.
  cancelJobDoctorLever(analysisId: EntityId, lever: string) {
    return this.axiosInstance.delete<JobDoctorPendingResponse>(
      `${ENDPOINTS.jobPositions.root}/analyses/${analysisId}/pending_levers`,
      { data: { lever } }
    );
  }

  getRecommendedWorkersCount({ jobPositionId }: { jobPositionId: string }) {
    return this.axiosInstance.get<{ count: number }>(
      `${ENDPOINTS.jobPositions.root}/${jobPositionId}/recommended_workers_count`
    );
  }

  /**
   * Validate a job position ad.
   *
   * Accepts either:
   * - `{ jobPositionId }` - validates an existing JP from DB (with rate limiting)
   * - `{ jpData }` - validates raw JP data (org onboarding, no rate limit)
   *
   * Both modes use the same backend endpoint with different params.
   */
  validateAd(params: { jobPositionId: string } | { jpData: Record<string, unknown>; rateLimitKey?: string }) {
    const requestBody =
      'jobPositionId' in params
        ? { id: params.jobPositionId }
        : {
            job_position_data: params.jpData,
            ...('rateLimitKey' in params && params.rateLimitKey ? { rate_limit_key: params.rateLimitKey } : {}),
          };

    return this.axiosInstance.post<ValidateAdResponse>(`${ENDPOINTS.jobPositions.root}/validate_ad`, requestBody);
  }

  checkPaymentReadiness() {
    return this.axiosInstance.get<PaymentReadinessResponse>(`${ENDPOINTS.jobPositions.root}/payment_readiness`);
  }

  goOnline({ jobPositionId }: { jobPositionId: string }) {
    return this.axiosInstance.post<{ message: string }>(`${ENDPOINTS.jobPositions.root}/${jobPositionId}/go_online`);
  }

  indexAssessmentQuestions({ jobPositionId }: { jobPositionId: string }) {
    return this.axiosInstance.get<JobPositionAssessmentQuestion[]>(ENDPOINTS.jobPositions.assessmentQuestions, {
      params: { job_position_id: jobPositionId },
    });
  }

  updateAssessmentQuestions({ jobPositionId, questions }: { jobPositionId: string; questions: string[] }) {
    return this.axiosInstance.put<JobPositionAssessmentQuestion[]>(ENDPOINTS.jobPositions.assessmentQuestions, {
      job_position_id: jobPositionId,
      questions,
    });
  }

  deleteAssessmentQuestion({ id }: { id: JobPositionAssessmentQuestion['id'] }) {
    return this.axiosInstance.delete(`${ENDPOINTS.jobPositions.assessmentQuestions}/${id}`);
  }
}

export const jobPositionService = ({ baseUrl, jwt }: { baseUrl: string; jwt?: string }) => {
  return new ServiceJobPositions(dynamicAxios(baseUrl, jwt));
};

export function useJobPositionsService() {
  const { baseUrl, jwt } = useServices();
  return useMemo(() => new ServiceJobPositions(dynamicAxios(baseUrl, jwt)), [baseUrl, jwt]);
}

export default useJobPositionsService;

export * from './types';
